/**
 * EdgeLabel — names the edge you're hovering, pinned to the edge itself.
 *
 * Edges are 1px curves with nowhere to put text, and a sleeve stacks
 * several moves on one arc, so "what am I pointing at?" was ambiguous
 * until you clicked. This rides the arc's MIDPOINT in world space (drei
 * <Html>, same trick HoverCard uses), so the label is unmistakably tied
 * to the line rather than floating in a corner of the canvas.
 *
 * It reads the hovered sleeve from the store (`hoveredSleeveIndex`) and
 * the specific move from `previewMoveId`, then evaluates the SAME
 * subArcControl curve Edges tessellates — including fan offsets and the
 * bidirectional two-lane split — so the label sits on the arc the cursor
 * is actually over, not on some averaged chord.
 */
import { Html } from '@react-three/drei';
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useAppStore } from '../state/store';
import type { Counter, Graph, Move, Position } from '../types/domain';
import { subArcControl } from './bezier';
import { nodeRadius } from './sizing';
import type { ForceLayout } from './useForceLayout';

const MAX_SUB_ARCS = 12;
/** Mirrors Edges.tsx so the label uses the same visible span. */
const EDGE_NODE_PADDING = 0.03;
/** Samples used to walk the arc by LENGTH rather than by t. */
const ARC_SAMPLES = 24;

export function EdgeLabel({
  graph,
  layout,
  positions,
  moves,
  counters,
}: {
  graph: Graph;
  layout: ForceLayout;
  positions: Position[];
  moves: Move[];
  counters: Counter[];
}) {
  // Must match what Nodes.tsx draws, or edges end short of the sphere
  // or plunge through it — see the note at the top of sizing.ts.
  const sizeBy = useAppStore((s) => s.sizeBy);
  const previewMoveId = useAppStore((s) => s.previewMoveId);
  const hoveredSleeveIndex = useAppStore((s) => s.hoveredSleeveIndex);
  const sleevesExpanded = useAppStore((s) => s.sleevesExpanded);
  const groupRef = useRef<THREE.Group>(null!);
  const tmpA = useRef(new THREE.Vector3());
  const tmpB = useRef(new THREE.Vector3());

  const entity = useMemo(() => {
    if (!previewMoveId) return null;
    const m = moves.find((x) => x.id === previewMoveId);
    if (m) return { name: m.name, from: m.from, to: m.to };
    const c = counters.find((x) => x.id === previewMoveId);
    if (c) return { name: c.name, from: c.from, to: c.result };
    return null;
  }, [previewMoveId, moves, counters]);

  // Which sleeve + sub-arc the cursor is on. Falls back to locating the
  // sleeve that carries this move when the hover came from a list row
  // rather than the canvas.
  const target = useMemo(() => {
    if (!previewMoveId) return null;
    let idx = hoveredSleeveIndex;
    if (idx === null || idx === undefined || graph.edges[idx]?.kind !== 'move') {
      idx = graph.edges.findIndex(
        (e) =>
          e.kind === 'move' &&
          (e.moveId === previewMoveId ||
            (e.moveIds?.includes(previewMoveId) ?? false)),
      );
      if (idx < 0) return null;
    }
    const e = graph.edges[idx];
    if (!e || e.kind !== 'move') return null;
    const moveCount = Math.min(e.moveIds?.length ?? 1, MAX_SUB_ARCS);
    const isSleeve = moveCount > 1;
    const expanded =
      isSleeve && (sleevesExpanded === 'expanded' || hoveredSleeveIndex === idx);
    // On an expanded sleeve, label the specific sub-arc under the cursor.
    const arcIdx = expanded
      ? Math.max(0, e.moveIds?.indexOf(previewMoveId) ?? 0)
      : 0;
    const srcNode = graph.byId.get(e.source);
    const tgtNode = graph.byId.get(e.target);
    return {
      e,
      moveCount,
      expanded,
      arcIdx,
      srcR: srcNode ? nodeRadius(srcNode, sizeBy) : 0.8,
      tgtR: tgtNode ? nodeRadius(tgtNode, sizeBy) : 0.8,
    };
  }, [previewMoveId, hoveredSleeveIndex, graph, sleevesExpanded]);

  useFrame(() => {
    const g = groupRef.current;
    const pos = layout.posRef.current;
    if (!g || !pos || !target) return;
    const si = layout.nodeIndex.get(target.e.source);
    const ti = layout.nodeIndex.get(target.e.target);
    if (si === undefined || ti === undefined) return;

    const ax = pos[si * 3], ay = pos[si * 3 + 1], az = pos[si * 3 + 2];
    const bx = pos[ti * 3], by = pos[ti * 3 + 1], bz = pos[ti * 3 + 2];
    const { cx, cy, cz } = subArcControl(
      ax, ay, az,
      bx, by, bz,
      target.arcIdx,
      target.moveCount,
      target.expanded,
      target.e.dirSign ?? 0,
    );

    // Two corrections are needed to actually land in the middle of the
    // line the user sees:
    //
    //  1. The drawn arc is CLIPPED at both ends so it stops on each
    //     node's sphere. The endpoints have different radii, so the
    //     visible span isn't centred on t = 0.5.
    //  2. t is not proportional to distance along a bezier. With a
    //     laterally-offset control point (fanned sleeves, bidirectional
    //     lanes) the curve is asymmetric, so t = 0.5 sits noticeably off
    //     the geometric middle.
    //
    // So: clip with the same formula Edges uses, then walk the arc by
    // LENGTH and stop at the halfway point.
    const aToC = Math.hypot(cx - ax, cy - ay, cz - az) || 1;
    const bToC = Math.hypot(cx - bx, cy - by, cz - bz) || 1;
    const tStart = Math.min(0.45, (target.srcR + EDGE_NODE_PADDING) / (2 * aToC));
    const tEnd = Math.max(0.55, 1 - (target.tgtR + EDGE_NODE_PADDING) / (2 * bToC));

    const at = (t: number, out: THREE.Vector3) => {
      const it = 1 - t;
      out.set(
        it * it * ax + 2 * it * t * cx + t * t * bx,
        it * it * ay + 2 * it * t * cy + t * t * by,
        it * it * az + 2 * it * t * cz + t * t * bz,
      );
      return out;
    };

    // Pass 1: cumulative length along the visible span.
    const cur = tmpA.current;
    const prev = tmpB.current;
    at(tStart, prev);
    let total = 0;
    for (let i = 1; i <= ARC_SAMPLES; i++) {
      at(tStart + ((tEnd - tStart) * i) / ARC_SAMPLES, cur);
      total += cur.distanceTo(prev);
      prev.copy(cur);
    }

    // Pass 2: walk again and stop at half the total length.
    const half = total * 0.5;
    at(tStart, prev);
    let acc = 0;
    let midT = (tStart + tEnd) * 0.5;
    for (let i = 1; i <= ARC_SAMPLES; i++) {
      const t = tStart + ((tEnd - tStart) * i) / ARC_SAMPLES;
      at(t, cur);
      const seg = cur.distanceTo(prev);
      if (acc + seg >= half) {
        // Interpolate within this segment for a smooth, jitter-free spot.
        const f = seg > 1e-6 ? (half - acc) / seg : 0;
        midT = t - ((tEnd - tStart) / ARC_SAMPLES) * (1 - f);
        break;
      }
      acc += seg;
      prev.copy(cur);
    }
    at(midT, cur);
    g.position.copy(cur);
  });

  if (!entity || !target) return null;

  const nameOf = (id: string) =>
    positions.find((p) => p.id === id)?.name ?? id;

  return (
    <group ref={groupRef}>
      <Html
        position={[0, 0, 0]}
        zIndexRange={[90, 0]}
        // Never intercept the pointer — this floats right over the line
        // the user is hovering, and capturing events would kill the very
        // hover that spawned it.
        style={{ pointerEvents: 'none' }}
      >
        <div
          style={{
            // Centre on the arc, lifted just clear of the line.
            transform: 'translate(-50%, calc(-100% - 8px))',
            padding: '4px 10px',
            borderRadius: 999,
            background: 'rgba(18,22,33,0.82)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.16)',
            boxShadow: '0 6px 18px rgba(0,0,0,0.45)',
            color: '#e9ecf1',
            fontFamily: 'var(--bjjmap-font-ui)',
            fontSize: 12,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            userSelect: 'none',
          }}
        >
          <span style={{ fontWeight: 600 }}>{entity.name}</span>
          <span style={{ color: '#9aa1ac', marginLeft: 7, fontSize: 11 }}>
            {nameOf(entity.from)} → {nameOf(entity.to)}
          </span>
        </div>
      </Html>
    </group>
  );
}
